import { useState, useEffect, useMemo, useRef } from "react";
import api from "@/lib/api";
import { t } from "@/lib/i18n";
import { toast } from "sonner";
import { MagnifyingGlass, CaretDown, Check } from "@phosphor-icons/react";

export const ModelPicker = ({ value, onSaved }) => {
  const [catalog, setCatalog] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [brand, setBrand] = useState("All");
  const boxRef = useRef(null);

  useEffect(() => { api.get("/llm/catalog").then((r) => setCatalog(r.data.models || [])).catch(() => {}); }, []);
  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const brands = useMemo(() => {
    const counts = {};
    catalog.forEach((m) => { if (!m.auto) counts[m.brand] = (counts[m.brand] || 0) + 1; });
    return [["All", catalog.filter((m) => !m.auto).length], ...Object.entries(counts).sort((a, b) => b[1] - a[1])];
  }, [catalog]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return catalog.filter((m) => (brand === "All" || m.brand === brand || m.auto) &&
      (!q || m.name.toLowerCase().includes(q) || m.id.toLowerCase().includes(q) || m.brand.toLowerCase().includes(q))).slice(0, 120);
  }, [catalog, search, brand]);

  const current = catalog.find((m) => m.id === value);

  const pick = async (m) => {
    try {
      await api.put("/me/llm-preference", { provider: m.provider, model: m.id });
      toast.success(t("settings.pref_saved", "AI model preference saved"));
      setOpen(false);
      onSaved && onSaved(m);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const price = (m) => (m.auto ? t("settings.smart_routing", "Picks the best model for each message")
    : m.prompt_per_m != null ? `${m.brand} · $${m.prompt_per_m} in · $${m.completion_per_m} out /1M` : `${m.brand} · API`);

  return (
    <div className="relative" ref={boxRef}>
      <button data-testid="model-picker-btn" onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between border border-gray-300 rounded-md px-3 py-2 text-sm bg-white hover:border-blue-400 transition-colors">
        <span className="truncate">
          {current ? <><span className="font-medium">{current.name}</span> <span className="text-gray-400 text-xs ml-1">{current.brand}</span></>
            : value ? <span className="font-mono">{value}</span>
            : <span className="text-gray-400">{t("settings.pick_model", "Choose a model…")}</span>}
        </span>
        <CaretDown size={14} className="text-gray-400 shrink-0" />
      </button>
      {open && (
        <div data-testid="model-picker-panel" className="absolute z-40 mt-1 w-full md:w-[560px] bg-white border border-gray-200 rounded-lg shadow-xl">
          <div className="p-2 border-b border-gray-100 flex items-center gap-2">
            <MagnifyingGlass size={14} className="text-gray-400" />
            <input data-testid="model-search-input" autoFocus value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={t("settings.search_models", "Search models…")}
              className="w-full text-sm focus:outline-none" />
          </div>
          <div className="flex max-h-[340px]">
            <div className="w-32 shrink-0 overflow-y-auto border-r border-gray-100 py-1">
              {brands.map(([b, n]) => (
                <button key={b} data-testid={`brand-filter-${b.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => setBrand(b)}
                  className={`w-full text-left px-3 py-1.5 text-xs truncate ${brand === b ? "bg-blue-600 text-white rounded-r-full font-semibold" : "text-gray-600 hover:bg-gray-50"}`}>
                  {b} <span className="opacity-60">({n})</span>
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto py-1">
              {shown.map((m) => (
                <button key={m.id} data-testid={`model-option-${m.id.replace(/[^a-z0-9]+/gi, "-")}`} onClick={() => pick(m)}
                  className={`w-full text-left px-4 py-2 hover:bg-blue-50 ${value === m.id ? "bg-blue-50" : ""}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900 truncate">{m.name}
                      {m.auto && <span className="ml-2 text-[10px] font-bold bg-blue-100 text-blue-700 rounded px-1.5 py-px uppercase">{t("settings.smart", "Smart routing")}</span>}
                      {m.direct && <span className="ml-2 text-[10px] font-bold bg-gray-100 text-gray-500 rounded px-1.5 py-px">API</span>}
                    </span>
                    {value === m.id && <Check size={14} className="text-blue-600 shrink-0" />}
                  </div>
                  <div className="text-[11px] text-gray-400">{price(m)}</div>
                </button>
              ))}
              {shown.length === 0 && <p className="text-xs text-gray-400 px-4 py-6 text-center">{t("settings.no_models", "No models match your search")}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
